import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, CONTENT_COMPONENT, EVENT_TYPE, RESOLVE_METHOD } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { getAnnotationInlineNodeTypes, getRangeInlineNodeNames } from '@atlaskit/editor-common/utils';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { closeComponent, createAnnotation, removeInlineCommentNearSelection, setInlineCommentDraftState, updateInlineCommentResolvedState } from '../editor-commands';
import { getAllAnnotations, getAnnotationViewKey, getPluginState, getSelectionPositions } from '../pm-plugins/utils';
import { AnnotationTestIds } from '../types';
import { AnnotationViewWrapper } from './AnnotationViewWrapper';
var findPosForDOM = function findPosForDOM(sel) {
  var $from = sel.$from,
    from = sel.from;

  // Retrieve current TextNode
  var index = $from.index();
  var node = index < $from.parent.childCount && $from.parent.child(index);

  // Right edge of a mark.
  if (!node && $from.nodeBefore && $from.nodeBefore.isText && $from.nodeBefore.marks.find(function (mark) {
    return mark.type.name === 'annotation';
  })) {
    return from - 1;
  }
  return from;
};
export function InlineCommentView(_ref) {
  var providers = _ref.providers,
    editorView = _ref.editorView,
    editorAnalyticsAPI = _ref.editorAnalyticsAPI,
    editorAPI = _ref.editorAPI,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent;
  // As inlineComment is the only annotation present, this function is not generic
  var inlineCommentProvider = providers.inlineComment;
  var state = editorView.state,
    dispatch = editorView.dispatch;
  var CreateComponent = inlineCommentProvider.createComponent,
    ViewComponent = inlineCommentProvider.viewComponent;
  var inlineCommentState = getPluginState(state);

  // bookmark
  var bookmarkSelector = useSharedPluginStateSelector(editorAPI, 'annotation.bookmark', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var bookmark = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? bookmarkSelector : inlineCommentState === null || inlineCommentState === void 0 ? void 0 : inlineCommentState.bookmark;

  // selectedAnnotations
  var selectedAnnotationsSelector = useSharedPluginStateSelector(editorAPI, 'annotation.selectedAnnotations', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var selectedAnnotations = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? selectedAnnotationsSelector : inlineCommentState === null || inlineCommentState === void 0 ? void 0 : inlineCommentState.selectedAnnotations;

  // annotations
  var annotationsSelector = useSharedPluginStateSelector(editorAPI, 'annotation.annotations', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var annotations = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? annotationsSelector : inlineCommentState === null || inlineCommentState === void 0 ? void 0 : inlineCommentState.annotations;

  // isInlineCommentViewClosed
  var isInlineCommentViewClosedSelector = useSharedPluginStateSelector(editorAPI, 'annotation.isInlineCommentViewClosed', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isInlineCommentViewClosed = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isInlineCommentViewClosedSelector : inlineCommentState === null || inlineCommentState === void 0 ? void 0 : inlineCommentState.isInlineCommentViewClosed;

  // isOpeningMediaCommentFromToolbar
  var isOpeningMediaCommentFromToolbarSelector = useSharedPluginStateSelector(editorAPI, 'annotation.isOpeningMediaCommentFromToolbar', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  var isOpeningMediaCommentFromToolbar = editorExperiment('platform_editor_usesharedpluginstateselector', true) ? isOpeningMediaCommentFromToolbarSelector : inlineCommentState === null || inlineCommentState === void 0 ? void 0 : inlineCommentState.isOpeningMediaCommentFromToolbar;
  var annotationsList = getAllAnnotations(editorView.state.doc);
  var selection = getSelectionPositions(state, editorExperiment('platform_editor_usesharedpluginstateselector', true) ? {
    bookmark: bookmark
  } : inlineCommentState);
  var position = findPosForDOM(selection);
  var dom;
  try {
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    dom = findDomRefAtPos(position, editorView.domAtPos.bind(editorView));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error);
    if (dispatchAnalyticsEvent) {
      var payload = {
        action: ACTION.ERRORED,
        actionSubject: ACTION_SUBJECT.CONTENT_COMPONENT,
        eventType: EVENT_TYPE.OPERATIONAL,
        attributes: {
          component: CONTENT_COMPONENT.INLINE_COMMENT,
          selection: selection.toJSON(),
          position: position,
          docSize: editorView.state.doc.nodeSize,
          error: error.toString()
        }
      };
      dispatchAnalyticsEvent(payload);
    }
  }

  // selectAnnotationMethod
  var selectAnnotationMethodSelector = useSharedPluginStateSelector(editorAPI, 'annotation.selectAnnotationMethod', {
    disabled: editorExperiment('platform_editor_usesharedpluginstateselector', false)
  });
  if (!dom) {
    return null;
  }

  // Create Component
  if (bookmark) {
    if (!CreateComponent) {
      return null;
    }
    var inlineNodeTypes = getRangeInlineNodeNames({
      doc: state.doc,
      pos: selection
    });

    //getting all text between bookmarked positions
    var textSelection = state.doc.textBetween(selection.from, selection.to);
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": AnnotationTestIds.floatingComponent,
      "data-editor-popup": "true"
    }, /*#__PURE__*/React.createElement(CreateComponent, {
      dom: dom,
      textSelection: textSelection,
      onCreate: function onCreate(id) {
        if (!fg('platform_editor_comments_api_manager')) {
          var createAnnotationResult = createAnnotation(editorAnalyticsAPI, editorAPI)(id, AnnotationTypes.INLINE_COMMENT, inlineCommentProvider.supportedBlockNodes)(editorView.state, editorView.dispatch);
          !editorView.hasFocus() && editorView.focus();
          if (!createAnnotationResult && fg('confluence_frontend_handle_annotation_error')) {
            throw new Error('Failed to create annotation');
          }
        }
      },
      onClose: function onClose() {
        if (!fg('platform_editor_comments_api_manager')) {
          setInlineCommentDraftState(editorAnalyticsAPI)(false)(editorView.state, editorView.dispatch);
          !editorView.hasFocus() && editorView.focus();
        }
      },
      inlineNodeTypes: inlineNodeTypes,
      isOpeningMediaCommentFromToolbar: isOpeningMediaCommentFromToolbar
    }));
  }

  // View Component
  var activeAnnotations = (selectedAnnotations === null || selectedAnnotations === void 0 ? void 0 : selectedAnnotations.filter(function (mark) {
    return annotations && annotations[mark.id] === false;
  })) || [];
  if (!ViewComponent || activeAnnotations.length === 0) {
    return null;
  }
  var onAnnotationViewed = function onAnnotationViewed() {
    var _editorView$state$doc;
    if (!dispatchAnalyticsEvent) {
      return;
    }

    // fire analytics
    var payload = {
      action: ACTION.VIEWED,
      actionSubject: ACTION_SUBJECT.ANNOTATION,
      actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
      eventType: EVENT_TYPE.TRACK,
      attributes: {
        overlap: activeAnnotations.length ? activeAnnotations.length - 1 : 0,
        targetNodeType: (_editorView$state$doc = editorView.state.doc.nodeAt(position)) === null || _editorView$state$doc === void 0 ? void 0 : _editorView$state$doc.type.name,
        method: editorExperiment('platform_editor_tables_table_selector', true) ? selectAnnotationMethodSelector : inlineCommentState === null || inlineCommentState === void 0 ? void 0 : inlineCommentState.selectAnnotationMethod
      }
    };
    dispatchAnalyticsEvent(payload);
  };
  if (isInlineCommentViewClosed || !selectedAnnotations) {
    return null;
  }

  // For view mode, the finding of inline node types is a bit more complex,
  // that's why we will not provide it as a `inlineNodeTypes` props to the view component,
  // to speed up the rendering process.
  var getInlineNodeTypes = function getInlineNodeTypes(annotationId) {
    return getAnnotationInlineNodeTypes(editorView.state, annotationId);
  };
  return /*#__PURE__*/React.createElement(AnnotationViewWrapper, {
    "data-editor-popup": "true",
    "data-testid": AnnotationTestIds.floatingComponent,
    key: getAnnotationViewKey(activeAnnotations),
    onViewed: onAnnotationViewed
  }, /*#__PURE__*/React.createElement(ViewComponent, {
    annotationsList: annotationsList,
    annotations: activeAnnotations,
    getInlineNodeTypes: getInlineNodeTypes,
    dom: dom,
    onDelete: function onDelete(id) {
      return removeInlineCommentNearSelection(id, inlineCommentProvider.supportedBlockNodes)(editorView.state, dispatch);
    },
    onResolve: function onResolve(id) {
      return updateInlineCommentResolvedState(editorAnalyticsAPI)(_defineProperty({}, id, true), RESOLVE_METHOD.COMPONENT)(editorView.state, editorView.dispatch);
    },
    onClose: function onClose() {
      closeComponent()(editorView.state, editorView.dispatch);
    },
    isOpeningMediaCommentFromToolbar: isOpeningMediaCommentFromToolbar
  }));
}